import { Spinner, Typography } from '@tempmee/react';
import { useCallback, useRef, type CSSProperties as ReactCSSProperties } from 'react';
import { colors } from '@tempmee/tailwind';
import { Center, SortingArrow, type TSortValue } from '~/commonComponents';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import { EmptyState } from './EmptyState';
import { HirePopupConfirmation } from './HirePopup';
import { ListItem } from './ListItem';
import { RejectPopupConfirmation } from './RejectPopup';

export const Styles = {
  Table: {
    borderSpacing: '0 12px',
    borderCollapse: 'separate',
  } as ReactCSSProperties,
  TableHeader: {
    ActionCol: {
      paddingLeft: 40,
      textAlign: 'left',
      width: '25%',
    } as ReactCSSProperties,
    AvatarCol: {
      textAlign: 'left',
      width: 96,
    } as ReactCSSProperties,
    CandidateCol: {
      textAlign: 'left',
    } as ReactCSSProperties,
    ProcessStepCol: {
      textAlign: 'left',
      width: '15%',
    } as ReactCSSProperties,
    ShortlistCol: {
      textAlign: 'left',
      width: '12%',
    } as ReactCSSProperties,
  },
};

interface ICandidateListTableProps {
  data: ICandidateListViewEntity[];
  isLoading: boolean;
  jobPostingId: string;
  sorting: TSortValue;
  onSortingChange?: (value: TSortValue | undefined) => void;
}

export const CandidateListTable = ({
  data,
  isLoading,
  jobPostingId,
  sorting,
  onSortingChange,
}: ICandidateListTableProps) => {
  const [isHireOpen, controlsHire] = useToggle();
  const [isRejectOpen, controlsReject] = useToggle();
  const targetedCandidate = useRef<
    Pick<ICandidateListViewEntity, 'id' | 'professional'> | undefined
  >(undefined);

  const isEmptyData = data.length === 0;

  const handleHireClicked = useCallback(
    (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => {
      targetedCandidate.current = candidate;
      controlsHire.setOn();
    },
    [controlsHire],
  );

  const handleRejectClicked = useCallback(
    (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => {
      targetedCandidate.current = candidate;
      controlsReject.setOn();
    },
    [controlsReject],
  );

  if (!isLoading && isEmptyData) {
    return <EmptyState />;
  }

  return (
    <>
      <table style={Styles.Table}>
        <thead>
          <tr>
            <th style={Styles.TableHeader.AvatarCol} />
            <th style={Styles.TableHeader.CandidateCol}>
              <Typography as="span" bold color={colors.primary[500]} size="body_large">
                Candidates
              </Typography>
            </th>
            <th style={Styles.TableHeader.ProcessStepCol}>
              <SortingArrow
                name="status"
                sorting={sorting}
                style={{ justifyContent: 'flex-start' }}
                variant="primary"
                onChange={onSortingChange}
              >
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Process Step
                </Typography>
              </SortingArrow>
            </th>
            <th style={Styles.TableHeader.ShortlistCol}>
              <SortingArrow
                name="shortlist"
                sorting={sorting}
                variant="primary"
                onChange={onSortingChange}
              >
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Shortlist
                </Typography>
              </SortingArrow>
            </th>
            <th style={Styles.TableHeader.ActionCol}>
              <Typography as="span" bold color={colors.primary[500]} size="body_large">
                Actions
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            data.map((it) => (
              <tr key={it.id}>
                <td colSpan={5}>
                  <ListItem
                    data={it}
                    jobPostingId={jobPostingId}
                    onHireClick={handleHireClicked}
                    onRejectClick={handleRejectClicked}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {isLoading && (
        <Center marginBlock={32}>
          <Spinner centered variant="default" size="md" />
        </Center>
      )}
      {isHireOpen && targetedCandidate.current && (
        <HirePopupConfirmation
          controls={controlsHire}
          candidateId={targetedCandidate.current.id}
          professionalName={targetedCandidate.current.professional.name}
        />
      )}
      {isRejectOpen && targetedCandidate.current && (
        <RejectPopupConfirmation
          controls={controlsReject}
          candidateId={targetedCandidate.current.id}
          professionalName={targetedCandidate.current.professional.name}
        />
      )}
    </>
  );
};
